<template>
  <div class="cargas-masivas">
    <v-row class="ma-0 my-4 align-center">
      <h1 class="text-h5 font-weight-bold">Cargas Masivas</h1>
    </v-row>

    <!-- ─── Dialog de confirmación ─── -->
    <v-dialog v-model="confirmDialog" max-width="440">
      <v-card>
        <v-card-title class="text-h6">¿Estás seguro?</v-card-title>
        <v-card-text class="text-body-1">{{ confirmMessage }}</v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn text @click="confirmDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="onConfirmed">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── Barra de progreso de carga ─── -->
    <div v-if="submitting" class="mb-4">
      <v-progress-linear
        :value="progressTotal > 0 ? (progressCurrent / progressTotal) * 100 : 0"
        color="primary"
        height="8"
        rounded
        striped
      />
      <div class="d-flex justify-space-between caption grey--text mt-1 px-1">
        <span>
          <v-icon small color="grey" class="mr-1">mdi-table-arrow-up</v-icon>
          Procesando carga masiva...
        </span>
        <span>{{ progressCurrent }}/{{ progressTotal }} usuarios</span>
      </div>
    </div>

    <!-- ─── Snackbar ─── -->
    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- ─── Panel de resultado ─── -->
    <div v-if="result">
      <v-row class="ma-0 mb-4 align-center justify-space-between">
        <div class="text-h6 font-weight-medium">
          Resultado — {{ category === 'altas' ? 'Altas' : category === 'nomina' ? 'Nómina' : 'Bajas' }}
        </div>
        <div class="d-flex align-center" style="gap: 8px">
          <v-btn
            v-if="failedCount > 0 && can('bulk-uploads:export-failures')"
            outlined
            color="error"
            small
            @click="downloadFailedCSV"
          >
            <v-icon left small>mdi-download</v-icon>Descargar fallidos (CSV)
          </v-btn>
          <v-btn text color="primary" @click="resetAll">
            <v-icon left>mdi-refresh</v-icon>Nueva carga
          </v-btn>
        </div>
      </v-row>

      <!-- Altas: resumen numérico -->
      <template v-if="category === 'altas'">
        <v-row class="mb-4">
          <v-col v-for="metric in ALTAS_METRICS" :key="metric.key" cols="6" sm="4" md="2">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold" :class="metric.color">{{ result[metric.key] }}</div>
              <div class="caption grey--text">{{ metric.label }}</div>
            </v-card>
          </v-col>
        </v-row>

        <div v-if="result.failedEntries && result.failedEntries.length > 0" class="mb-4">
          <div class="cargas-masivas__section-label mb-2">
            <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
            Entradas fallidas ({{ result.failedEntries.length }})
          </div>
          <v-simple-table dense class="elevation-1">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Apellido</th>
                  <th>RUT</th>
                  <th>Email</th>
                  <th v-if="withPlan">Plan</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, i) in result.failedEntries" :key="i" class="blue-grey lighten-5">
                  <td>{{ entry.name }}</td>
                  <td>{{ entry.surname }}</td>
                  <td>{{ entry.rut }}</td>
                  <td>{{ entry.email }}</td>
                  <td v-if="withPlan">{{ planName(entry.planId) }}</td>
                  <td>
                    <span class="error--text caption">{{ entry.errorCode }}: {{ entry.errorMessage }}</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </template>

      <!-- Nómina: resumen altas + bajas -->
      <template v-else-if="category === 'nomina'">
        <div class="text-subtitle-2 font-weight-bold mb-2">Altas aplicadas</div>
        <v-row class="mb-4">
          <v-col v-for="metric in ALTAS_METRICS" :key="'na-' + metric.key" cols="6" sm="4" md="2">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold" :class="metric.color">{{ result.altas[metric.key] }}</div>
              <div class="caption grey--text">{{ metric.label }}</div>
            </v-card>
          </v-col>
        </v-row>
        <div class="text-subtitle-2 font-weight-bold mb-2">Bajas aplicadas</div>
        <v-row class="mb-4">
          <v-col v-for="metric in BAJAS_METRICS" :key="'nb-' + metric.key" cols="6" sm="3">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold" :class="metric.color">{{ result.bajas[metric.key] }}</div>
              <div class="caption grey--text">{{ metric.label }}</div>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Bajas: resumen numérico -->
      <template v-else>
        <v-row class="mb-4">
          <v-col v-for="metric in BAJAS_METRICS" :key="metric.key" cols="6" sm="3">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold" :class="metric.color">{{ result[metric.key] }}</div>
              <div class="caption grey--text">{{ metric.label }}</div>
            </v-card>
          </v-col>
        </v-row>

        <div v-if="result.entries && result.entries.length > 0" class="mb-4">
          <div class="cargas-masivas__section-label mb-2">Detalle por RUT</div>
          <v-simple-table dense class="elevation-1">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>RUT</th>
                  <th v-if="withPlan">Plan</th>
                  <th>Encontrado</th>
                  <th>Cuentas restantes</th>
                  <th>Planes desactivados</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(entry, i) in result.entries"
                  :key="i"
                  :class="{ 'blue-grey lighten-5': !entry.found }"
                >
                  <td>{{ entry.rut }}</td>
                  <td v-if="withPlan">{{ planName(entry.planId) }}</td>
                  <td>
                    <v-icon small :color="entry.found ? 'primary' : 'error'">
                      {{ entry.found ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </td>
                  <td>{{ entry.totalAccountsRemaining !== undefined ? entry.totalAccountsRemaining : '—' }}</td>
                  <td>{{ entry.deactivatedPlanIds ? entry.deactivatedPlanIds.length : 0 }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </template>
    </div>

    <!-- ─── Wizard ─── -->
    <v-card v-else outlined>

      <!-- Indicador de pasos -->
      <div class="cargas-masivas__steps">
        <div
          v-for="(label, idx) in stepLabels"
          :key="idx"
          class="cargas-masivas__step-item"
          :class="{
            'cargas-masivas__step-item--active': step === idx + 1,
            'cargas-masivas__step-item--done': step > idx + 1,
          }"
        >
          <div class="cargas-masivas__step-circle">
            <v-icon v-if="step > idx + 1" small color="white">mdi-check</v-icon>
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <span class="cargas-masivas__step-label d-none d-sm-inline">{{ label }}</span>
        </div>
      </div>
      <v-divider />

      <!-- ══ Step 1: Categoría ══ -->
      <div v-if="step === 1" class="pa-6">
        <div class="text-subtitle-1 font-weight-medium mb-4">Selecciona una categoría</div>
        <v-row class="mb-4">
          <v-col v-for="cat in allowedCategories" :key="cat.value" cols="12" sm="6" md="4">
            <v-card
              outlined
              class="pa-4 text-center cargas-masivas__cat-card"
              :class="{ 'cargas-masivas__cat-card--selected': category === cat.value }"
              @click="selectCategory(cat.value)"
            >
              <v-icon x-large :color="category === cat.value ? 'primary' : 'grey lighten-1'">
                {{ cat.icon }}
              </v-icon>
              <div class="text-subtitle-1 font-weight-bold mt-2">{{ cat.title }}</div>
              <div class="caption grey--text">{{ cat.subtitle }}</div>
            </v-card>
          </v-col>
        </v-row>
        <v-expand-transition>
          <v-alert
            v-if="category"
            :color="CATEGORY_INFO[category].color"
            :icon="CATEGORY_INFO[category].icon"
            outlined
            dense
            class="mb-4"
          >
            <div class="text-body-2 font-weight-medium mb-1">{{ CATEGORY_INFO[category].title }}</div>
            <div class="text-body-2">{{ CATEGORY_INFO[category].description }}</div>
            <ul class="mt-2 mb-0" style="padding-left: 18px">
              <li v-for="(point, i) in CATEGORY_INFO[category].points" :key="i" class="text-body-2">
                {{ point }}
              </li>
            </ul>
          </v-alert>
        </v-expand-transition>

        <v-btn color="primary" :disabled="!category" @click="step = 2">Continuar</v-btn>
      </div>

      <!-- ══ Step 2: Parámetros ══ -->
      <div v-if="step === 2" class="pa-6">
        <div class="text-subtitle-1 font-weight-medium mb-4">Parámetros de la carga</div>

        <!-- Altas / Nómina: solo contrato. El país y el envío de correos los define el
             contrato (sus preferencias de correo), no esta pantalla. -->
        <template v-if="category === 'altas' || category === 'nomina'">
          <v-row dense>
            <v-col cols="12" md="6" class="pb-0">
              <v-autocomplete
                v-model="params.contratoId"
                :items="contratos"
                item-text="label"
                item-value="id"
                label="Contrato *"
                dense outlined
                :loading="loadingContratos"
                clearable
              />
            </v-col>
          </v-row>
        </template>

        <!-- Bajas: solo contrato -->
        <template v-else>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="params.contratoId"
                :items="contratos"
                item-text="label"
                item-value="id"
                label="Contrato *"
                dense outlined
                :loading="loadingContratos"
                clearable
              />
            </v-col>
          </v-row>
        </template>

        <div class="d-flex mt-2">
          <v-btn text @click="step = 1" class="mr-2">Atrás</v-btn>
          <v-btn color="primary" :disabled="!step2Valid" @click="step = 3">Continuar</v-btn>
        </div>
      </div>

      <!-- ══ Step 3: Archivo ══ -->
      <div v-if="step === 3" class="pa-6">
        <div class="text-subtitle-1 font-weight-medium mb-4">Subir archivo</div>
        <div
          class="cargas-masivas__drop-zone mb-4"
          :class="{ 'cargas-masivas__drop-zone--active': isDragging }"
          @click="onDropZoneClick"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <v-icon large color="grey">mdi-cloud-upload-outline</v-icon>
          <div class="text-body-1 grey--text mt-2">Arrastra tu archivo aquí</div>
          <div class="grey--text my-1">— o —</div>
          <v-file-input
            ref="fileInput"
            v-model="file"
            label="Seleccionar archivo"
            accept=".xlsx,.xls,.csv"
            dense outlined
            prepend-icon=""
            hide-details
            style="max-width: 320px"
            @change="onFileSelected"
          />
          <div class="caption grey--text mt-2">Formatos aceptados: .xlsx, .xls, .csv</div>
        </div>

        <v-select
          v-if="sheetNames.length > 1"
          :value="selectedSheet"
          :items="sheetNames"
          label="Hoja del archivo"
          dense outlined
          style="max-width: 320px"
          class="mb-3"
          @change="loadSheet"
        />

        <v-alert v-if="parseError" type="error" dense outlined dismissible class="mb-3">
          {{ parseError }}
        </v-alert>

        <div v-if="fileColumns.length > 0" class="mb-3 text-body-2 primary--text">
          <v-icon small color="primary" class="mr-1">mdi-check-circle</v-icon>
          {{ fileColumns.length }} columna(s) y {{ fileRows.length }} fila(s) detectadas.
        </div>

        <div class="d-flex">
          <v-btn text @click="step = 2" class="mr-2">Atrás</v-btn>
          <v-btn color="primary" :disabled="!fileColumns.length" @click="step = 4">Continuar</v-btn>
        </div>
      </div>

      <!-- ══ Step 4: Mapeo de columnas ══ -->
      <div v-if="step === 4" class="pa-6">
        <div class="text-subtitle-1 font-weight-medium mb-4">Mapeo de columnas</div>
        <v-select
          v-if="sheetNames.length > 1"
          :value="selectedSheet"
          :items="sheetNames"
          label="Hoja del archivo"
          dense outlined
          style="max-width: 320px"
          class="mb-3"
          @change="loadSheet"
        />
        <v-alert v-if="parseError" type="error" dense outlined class="mb-3">
          {{ parseError }}
        </v-alert>
        <div class="text-body-2 grey--text mb-3">
          Columnas detectadas en el archivo:
          <v-chip v-for="col in fileColumns" :key="col" small outlined class="mr-1 mb-1">{{ col }}</v-chip>
        </div>
        <div v-for="section in fieldSections" :key="section.key" class="mb-2">
          <div class="cargas-masivas__section-label mb-2">{{ section.title }}</div>
          <v-row dense>
            <v-col v-for="field in section.fields" :key="field.key" cols="12" sm="6" md="4">
              <v-select
                v-model="mapping[field.key]"
                :items="fileColumns"
                :label="fieldLabel(field)"
                :multiple="!!field.multiple"
                :small-chips="!!field.multiple"
                dense outlined
                clearable
              />
            </v-col>
          </v-row>
        </div>
        <div class="d-flex mt-2">
          <v-btn text @click="step = 3" class="mr-2">Atrás</v-btn>
          <v-btn color="primary" :disabled="!mappingValid" @click="onGoToPreview">
            {{ withRelations ? 'Continuar' : 'Ver preview' }}
          </v-btn>
        </div>
      </div>

      <!-- ══ Relación de valores (solo si se mapeó la columna Plan o Género) ══ -->
      <div v-if="withRelations && step === RELATION_STEP" class="pa-6">
        <template v-if="withPlan">
          <div class="text-subtitle-1 font-weight-medium mb-2">Relación de planes</div>
          <div class="text-body-2 grey--text mb-4">
            Indica a qué plan del contrato corresponde cada valor de la columna Plan.
            "Todos los planes" deja el acceso sin restricción.
          </div>
          <v-progress-linear v-if="loadingContratoPlanes" indeterminate color="primary" class="mb-3" />
          <v-simple-table v-else dense class="elevation-1 mb-6">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Valor en el archivo</th>
                  <th>Filas</th>
                  <th>Plan del contrato</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in planValues" :key="item.key">
                  <td>{{ item.label }}</td>
                  <td>{{ item.count }}</td>
                  <td>
                    <v-select
                      :value="planRelation[item.key]"
                      :items="planRelationOptions"
                      placeholder="Selecciona un plan"
                      dense outlined hide-details
                      @change="value => $set(planRelation, item.key, value)"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </template>

        <template v-if="withGender">
          <div class="text-subtitle-1 font-weight-medium mb-2">Relación de género</div>
          <div class="text-body-2 grey--text mb-4">
            Indica a qué género corresponde cada valor de la columna Género.
            "Sin especificar" deja el dato vacío.
          </div>
          <v-simple-table dense class="elevation-1 mb-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Valor en el archivo</th>
                  <th>Filas</th>
                  <th>Género</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in genderValues" :key="item.key">
                  <td>{{ item.label }}</td>
                  <td>{{ item.count }}</td>
                  <td>
                    <v-select
                      :value="genderRelation[item.key]"
                      :items="GENDER_OPTIONS"
                      placeholder="Selecciona un género"
                      dense outlined hide-details
                      @change="value => $set(genderRelation, item.key, value)"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </template>

        <div class="d-flex">
          <v-btn text @click="step = RELATION_STEP - 1" class="mr-2">Atrás</v-btn>
          <v-btn color="primary" :disabled="!relationsValid" @click="goToPreview">
            Ver preview
          </v-btn>
        </div>
      </div>

      <!-- ══ Preview + Confirmar ══ -->
      <div v-if="step === previewStep" class="pa-6">
        <div class="text-subtitle-1 font-weight-medium mb-4">Vista previa y confirmar</div>

        <!-- Nómina: diff summary -->
        <template v-if="category === 'nomina'">
          <div v-if="nominaDiffLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-3 grey--text">Calculando diferencias con el estado actual...</div>
          </div>
          <template v-else-if="nominaDiffData">
            <div class="cargas-masivas__section-label mb-3">
              {{ preview.length }} RUT(s) en la nómina enviada
            </div>
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h5 font-weight-bold primary--text">{{ nominaDiffData.summary.toAddCount }}</div>
                  <div class="caption grey--text">Por dar de alta</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h5 font-weight-bold blue-grey--text text--darken-1">{{ nominaDiffData.summary.toRemoveCount }}</div>
                  <div class="caption grey--text">Por dar de baja</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h5 font-weight-bold">{{ nominaDiffData.summary.unchanged }}</div>
                  <div class="caption grey--text">Sin cambios</div>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </template>

        <!-- Altas / Bajas: tabla de preview -->
        <template v-else>
          <div class="cargas-masivas__section-label mb-2">
            {{ preview.length }} registro(s) listos para enviar
            <span v-if="mapping.mascotas">({{ totalPets }} mascota(s) en total)</span>
          </div>
          <div style="max-height: 380px; overflow: auto" class="mb-3">
            <v-simple-table dense class="elevation-1">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th v-for="field in requiredFields" :key="field.key">{{ field.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in preview.slice(0, PREVIEW_LIMIT)" :key="i">
                    <td v-for="field in requiredFields" :key="field.key">{{ previewCell(row, field) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <div v-if="preview.length > PREVIEW_LIMIT" class="caption grey--text mb-3">
            Mostrando los primeros {{ PREVIEW_LIMIT }} de {{ preview.length }} registros.
          </div>
        </template>

        <v-alert v-if="submitError" type="error" dense outlined dismissible class="mb-3">
          {{ submitError }}
        </v-alert>

        <div class="d-flex">
          <v-btn text @click="step = previewStep - 1" class="mr-2" :disabled="submitting">Atrás</v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="openConfirm"
          >
            <v-icon left>mdi-send</v-icon>
            {{ category === 'nomina' ? 'Aplicar nómina' : `Confirmar y enviar ${preview.length} registros` }}
          </v-btn>
        </div>
      </div>

    </v-card>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import ContratoService from '../services/contrato.service';
import CompanyService from '../services/companie.service';
import CargasMasivasService from '../services/cargas-masivas.service';
import { utilsMixin } from '../mixins/utilsMixin';
import RutUtils from '../utils/rut.utils';
import { can } from '../utils/permissions';

const PREVIEW_LIMIT = 50;
const MAX_FILE_SIZE_MB = 10;

const STEP_LABELS = ['Categoría', 'Parámetros', 'Archivo', 'Mapeo', 'Preview'];
// Con la columna Plan o Género mapeada, "Relación de valores" se intercala antes del preview.
const RELATION_STEP = 5;
// Valor de "Todos los planes" en la relación: el acceso queda sin plan (planId null).
const ALL_PLANS = '__ALL__';
// Valor de "Sin especificar" en la relación de género: no se envía género.
const NO_GENDER = '__NONE__';

const CATEGORIES = [
  {
    value: 'altas',
    title: 'Altas',
    subtitle: 'Dar de alta usuarios masivamente',
    icon: 'mdi-account-plus-outline',
    requiredTags: ['bulk-uploads:import'],
  },
  {
    value: 'bajas',
    title: 'Bajas',
    subtitle: 'Desactivar usuarios masivamente',
    icon: 'mdi-account-minus-outline',
    requiredTags: ['bulk-uploads:deactivate'],
  },
  {
    value: 'nomina',
    title: 'Nómina',
    subtitle: 'Sincronizar nómina completa',
    icon: 'mdi-account-sync-outline',
    // Nómina has its own tag; it runs both the bulk import and the bulk deactivate.
    requiredTags: ['bulk-uploads:sync-payroll'],
  },
];

const CATEGORY_INFO = {
  altas: {
    color: 'primary',
    icon: 'mdi-information-outline',
    title: '¿Qué hace esta acción?',
    description: 'Liga un contrato a usuarios. Si el usuario no existe en el sistema, se crea automáticamente.',
    points: [
      'Requiere: nombre, apellido, email y RUT de cada usuario.',
      'Opcionales: apellido materno, teléfono, fecha de nacimiento y género; solo se guardan al crear usuarios nuevos.',
      'Si el usuario no existe, se crea; si ya existe, se le asocia el contrato.',
      'Un usuario puede tener múltiples accesos en el mismo contrato.',
      'Columna Plan opcional: restringe el acceso del usuario a ese plan del contrato; sin ella, el acceso permite todos los planes.',
      'Los correos de bienvenida y de asignación se envían según las preferencias de correo del contrato.',
    ],
  },
  bajas: {
    color: 'error',
    icon: 'mdi-alert-outline',
    title: '¿Qué hace esta acción?',
    description: 'Disminuye las cuentas (accesos) de un usuario hacia el contrato de la compañía.',
    points: [
      'Requiere solo el RUT de cada usuario.',
      'Reduce las cuentas de acceso del usuario en el contrato.',
      'Columna Plan opcional: descuenta del acceso de ese plan (o del acceso sin plan si la celda está vacía).',
      'Columna Cantidad de mascotas opcional: descuenta esa cantidad de cuentas por fila (1 si viene vacía).',
      'Si el usuario tiene membresías activas, estas se desactivan en consecuencia.',
      'Esta acción es difícil de revertir — revisa bien el archivo antes de continuar.',
    ],
  },
  nomina: {
    color: 'blue-grey',
    icon: 'mdi-information-outline',
    title: '¿Qué hace esta acción?',
    description: 'Sincroniza la lista completa de usuarios activos del contrato.',
    points: [
      'Requiere la nómina completa (todos los RUTs que deben estar activos).',
      'Calcula automáticamente quiénes entran (altas) y quiénes salen (bajas).',
      'Columna Plan opcional: compara por RUT y plan, y restringe los accesos sin plan al plan de la nómina.',
      'Podrás revisar el resumen de cambios antes de confirmar.',
    ],
  },
};

// Secciones del paso de mapeo, en orden de aparición.
const MAPPING_SECTIONS = [
  { key: 'identity', title: 'Identificación' },
  { key: 'contact', title: 'Contacto' },
  { key: 'personal', title: 'Datos personales' },
  { key: 'policy', title: 'Póliza' },
];

const FIELDS = {
  rut: { key: 'rut', label: 'RUT', section: 'identity', multiple: true, separator: '' },
  nombre: { key: 'nombre', label: 'Nombre', section: 'identity', multiple: true, separator: ' ' },
  apellido: { key: 'apellido', label: 'Apellido Paterno', section: 'identity' },
  apellidoMaterno: { key: 'apellidoMaterno', label: 'Apellido Materno', section: 'identity', optional: true },
  nombreCompleto: { key: 'nombreCompleto', label: 'Nombre Completo', section: 'identity', optional: true },
  email: { key: 'email', label: 'Email', section: 'contact' },
  telefono: { key: 'telefono', label: 'Teléfono', section: 'contact', optional: true },
  fechaNacimiento: { key: 'fechaNacimiento', label: 'Fecha de nacimiento', section: 'personal', optional: true },
  genero: { key: 'genero', label: 'Género', section: 'personal', optional: true },
  plan: { key: 'plan', label: 'Plan', section: 'policy', optional: true },
  mascotas: { key: 'mascotas', label: 'Cantidad de mascotas', section: 'policy', optional: true },
  extraInfo: { key: 'extraInfo', label: 'ExtraInfo', section: 'policy', optional: true },
};

// Dentro de cada sección, los obligatorios van primero.
const ALTAS_FIELDS = [
  FIELDS.rut, FIELDS.nombre, FIELDS.apellido, FIELDS.apellidoMaterno, FIELDS.nombreCompleto,
  FIELDS.email, FIELDS.telefono,
  FIELDS.fechaNacimiento, FIELDS.genero,
  FIELDS.plan, FIELDS.mascotas, FIELDS.extraInfo,
];

const BAJAS_FIELDS = [FIELDS.rut, FIELDS.plan, FIELDS.mascotas];

// La nómina solo exige el RUT.
const NOMINA_FIELDS = ALTAS_FIELDS.map(f => (f.key === 'rut' ? f : { ...f, optional: true }));

const COLUMN_ALIASES = {
  nombre: ['nombre', 'name', 'firstname', 'first name'],
  apellido: ['apellido', 'apellido paterno', 'apellido_paterno', 'apellido_pat', 'surname', 'lastname', 'last name'],
  apellidoMaterno: ['apellido materno', 'apellido_materno', 'apellido_mat', 'maternal surname'],
  nombreCompleto: ['nombre completo', 'nombrecompleto', 'nombre_completo', 'full name', 'fullname', 'nombre y apellido'],
  email: ['email', 'correo', 'mail'],
  telefono: ['telefono', 'teléfono', 'celular', 'fono', 'movil', 'móvil', 'phone'],
  fechaNacimiento: ['fecha de nacimiento', 'fecha nacimiento', 'fecha_nacimiento', 'fecha_nac', 'nacimiento', 'birthday', 'birthdate'],
  genero: ['genero', 'género', 'sexo', 'sex', 'gender'],
  rut: ['rut', 'run', 'rut-dv', 'rutdv', 'numero', 'número'],
  extraInfo: ['extrainfo', 'extra_info', 'extra info', 'info', 'informacion', 'información'],
  plan: ['plan', 'planes', 'plan contratado', 'producto'],
  mascotas: ['mascotas', 'cantidad mascotas', 'cantidad de mascotas', 'n mascotas', 'pets'],
};

// Valores aceptados por users.sex, más "Sin especificar".
const GENDER_OPTIONS = [
  { text: 'Masculino', value: 'male' },
  { text: 'Femenino', value: 'female' },
  { text: 'Otro', value: 'other' },
  { text: 'Sin especificar', value: NO_GENDER },
];
// Escrituras frecuentes, solo para sugerir la relación: el usuario la confirma o la cambia.
const GENDER_ALIASES = {
  male: ['masculino', 'hombre', 'male', 'man'],
  female: ['femenino', 'mujer', 'female', 'woman'],
  other: ['otro', 'otra', 'other', 'no binario'],
};

const ALTAS_METRICS = [
  { key: 'total', label: 'Total', color: 'blue-grey--text text--darken-1' },
  { key: 'created', label: 'Creados', color: 'primary--text' },
  { key: 'existing', label: 'Existentes', color: 'blue--text text--lighten-1' },
  { key: 'failed', label: 'Fallidos', color: 'error--text' },
  { key: 'emailSent', label: 'Emails enviados', color: 'primary--text' },
  { key: 'emailFailed', label: 'Emails fallidos', color: 'error--text' },
];

const BAJAS_METRICS = [
  { key: 'total', label: 'Total pólizas', color: 'blue-grey--text text--darken-1' },
  { key: 'fullyDeactivated', label: 'Dados de baja', color: 'primary--text' },
  { key: 'notFound', label: 'No encontrados', color: 'error--text' },
];

function buildEmptyParams() {
  return {
    contratoId: null,
  };
}

function valueKey(value) {
  return String(value || '').trim().toLowerCase();
}

// Not \b\w: JS treats accented letters as non-word chars, so "pérez" became "PéRez".
function capitalizeWords(text) {
  return text.toLowerCase().replace(/(^|[\s'-])([^\s'-])/g, (_, sep, c) => sep + c.toUpperCase());
}

// Unparseable or < 1 counts as a single pet: every row is at least one policy.
function parsePetCount(value) {
  const count = parseInt(value, 10);
  return count >= 1 ? count : 1;
}

function toIsoDate(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  const isRealDate =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  if (!isRealDate) return '';
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Excel keeps date cells as day serials; text dates in Chilean files are written day first.
function normalizeBirthday(value) {
  if (/^\d+(\.\d+)?$/.test(value)) {
    const parsed = XLSX.SSF.parse_date_code(Number(value));
    return parsed ? toIsoDate(parsed.y, parsed.m, parsed.d) : '';
  }
  const yearFirst = value.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (yearFirst) return toIsoDate(+yearFirst[1], +yearFirst[2], +yearFirst[3]);
  const dayFirst = value.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (dayFirst) return toIsoDate(+dayFirst[3], +dayFirst[2], +dayFirst[1]);
  return '';
}

// Valores ambiguos como "M" no se sugieren: puede ser Masculino o Mujer según la empresa.
function suggestGender(key) {
  return Object.keys(GENDER_ALIASES).find(code => GENDER_ALIASES[code].includes(key));
}

// Valores distintos de una columna (sin distinguir mayúsculas) con su cantidad de filas.
function distinctValues(rows, field) {
  const values = new Map();
  rows.forEach(row => {
    const key = valueKey(row[field]);
    if (!key) return;
    const item = values.get(key) || { key, label: row[field].trim(), count: 0 };
    item.count += 1;
    values.set(key, item);
  });
  return [...values.values()];
}

const NORMALIZERS = {
  rut: value => RutUtils.normalizeIdentifier(value),
  nombre: capitalizeWords,
  apellido: capitalizeWords,
  apellidoMaterno: capitalizeWords,
  email: value => value.toLowerCase(),
  telefono: value => value.replace(/[^\d+]/g, ''),
  fechaNacimiento: normalizeBirthday,
  mascotas: value => (value === '' ? '' : String(parsePetCount(value))),
};

function petsOf(row) {
  return row.mascotas ? Number(row.mascotas) : 1;
}

// The backend adds one account per repeated row, so N pets travel as N copies of the line.
function repeatByPets(line, row) {
  return Array(petsOf(row)).fill(line);
}

function splitFullName(fullName) {
  const words = fullName.trim().split(/\s+/);
  if (words.length >= 3) {
    return { apellido: words[0], apellidoMaterno: words[1], nombre: words.slice(2).join(' ') };
  }
  if (words.length === 2) {
    return { apellido: words[0], apellidoMaterno: '', nombre: words[1] };
  }
  return { apellido: '', apellidoMaterno: '', nombre: words[0] || '' };
}

function buildEmptyMapping(fields) {
  return fields.reduce((acc, f) => {
    acc[f.key] = f.multiple ? [] : null;
    return acc;
  }, {});
}

export default {
  name: 'CargasMasivas',

  mixins: [utilsMixin],

  data() {
    return {
      PREVIEW_LIMIT,
      RELATION_STEP,
      GENDER_OPTIONS,
      CATEGORY_INFO,
      ALTAS_METRICS,
      BAJAS_METRICS,

      step: 1,
      category: null,
      params: buildEmptyParams(),

      file: null,
      isDragging: false,
      parseError: null,
      workbook: null,
      sheetNames: [],
      selectedSheet: null,
      fileColumns: [],
      fileRows: [],

      mapping: {},

      preview: [],

      submitting: false,
      submitError: null,
      result: null,
      progressCurrent: 0,
      progressTotal: 0,

      confirmDialog: false,
      confirmMessage: '',

      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',

      nominaDiffData: null,
      nominaDiffLoading: false,

      contratos: [],

      loadingContratos: false,

      contratoPlanes: [],
      loadingContratoPlanes: false,
      // Valor normalizado de la columna Plan → planId del contrato o ALL_PLANS.
      planRelation: {},
      // Valor normalizado de la columna Género → male/female/other o NO_GENDER.
      genderRelation: {},
    };
  },

  computed: {
    /** Categories whose bulk action tags the admin holds (spec 132). */
    allowedCategories() {
      return CATEGORIES.filter((cat) => cat.requiredTags.every((tag) => can(tag)));
    },
    // La columna Plan está mapeada: las cargas usan el plan por fila.
    withPlan() {
      return !!this.mapping.plan;
    },
    withGender() {
      return !!this.mapping.genero;
    },
    // Columnas cuyos valores el usuario relaciona antes del preview.
    withRelations() {
      return this.withPlan || this.withGender;
    },
    stepLabels() {
      if (!this.withRelations) return STEP_LABELS;
      return [...STEP_LABELS.slice(0, RELATION_STEP - 1), 'Relación de valores', 'Preview'];
    },
    previewStep() {
      return this.withRelations ? RELATION_STEP + 1 : RELATION_STEP;
    },
    planValues() {
      return distinctValues(this.preview, 'plan');
    },
    genderValues() {
      return distinctValues(this.preview, 'genero');
    },
    // Campos del mapeo agrupados por sección; las secciones sin campos se omiten.
    fieldSections() {
      return MAPPING_SECTIONS
        .map(section => ({ ...section, fields: this.requiredFields.filter(f => f.section === section.key) }))
        .filter(section => section.fields.length > 0);
    },
    totalPets() {
      return this.preview.reduce((sum, row) => sum + petsOf(row), 0);
    },
    planRelationOptions() {
      return [
        { text: 'Todos los planes', value: ALL_PLANS },
        ...this.contratoPlanes.map(p => ({ text: p.plan.name, value: p.planId }))
      ];
    },
    relationsValid() {
      const plansValid =
        !this.withPlan || (!this.loadingContratoPlanes && this.planValues.every(item => !!this.planRelation[item.key]));
      const gendersValid = !this.withGender || this.genderValues.every(item => !!this.genderRelation[item.key]);
      return plansValid && gendersValid;
    },
    requiredFields() {
      if (this.category === 'altas') return ALTAS_FIELDS;
      if (this.category === 'nomina') return NOMINA_FIELDS;
      return BAJAS_FIELDS;
    },
    step2Valid() {
      return !!this.params.contratoId;
    },
    mappingValid() {
      const hasNombreCompleto = !!this.mapping.nombreCompleto;
      return this.requiredFields.every(f => {
        if (f.optional) return true;
        if (hasNombreCompleto && (f.key === 'nombre' || f.key === 'apellido')) return true;
        return f.multiple ? this.mapping[f.key].length > 0 : !!this.mapping[f.key];
      });
    },
    selectedContrato() {
      return this.contratos.find(c => c.id === this.params.contratoId) || null;
    },
    failedCount() {
      if (!this.result) return 0;
      if (this.category === 'altas') return this.result.failedEntries?.length || 0;
      if (this.category === 'bajas') return (this.result.entries || []).filter(e => e.reason).length;
      if (this.category === 'nomina') {
        return (this.result.altas?.failedEntries?.length || 0) +
               (this.result.bajas?.entries || []).filter(e => e.reason).length;
      }
      return 0;
    },
    canSubmit() {
      if (this.category === 'nomina') {
        if (!this.nominaDiffData || this.nominaDiffLoading) return false;
        return this.nominaDiffData.summary.toAddCount > 0 || this.nominaDiffData.summary.toRemoveCount > 0;
      }
      return this.preview.length > 0;
    },
  },

  methods: {
    can,
    selectCategory(value) {
      this.category = value;
      const fields = value === 'altas' ? ALTAS_FIELDS : value === 'nomina' ? NOMINA_FIELDS : BAJAS_FIELDS;
      this.mapping = buildEmptyMapping(fields);
      this.loadContratos();
    },

    async loadContratos() {
      this.loadingContratos = true;
      const [contRes, compRes] = await Promise.all([
        ContratoService.getAll(),
        CompanyService.getCompanies(1, 500, ''),
      ]);
      if (contRes.status === 'ok' && compRes.status === 'ok') {
        const companiesArr = compRes.data?.companies || compRes.data || [];
        this.contratos = (contRes.data || []).map(c => {
          const empresa = companiesArr.find(e => e.id === c.companyId);
          return { ...c, label: empresa ? `${c.codigo} — ${empresa.name}` : c.codigo };
        });
      }
      this.loadingContratos = false;
    },

    onDropZoneClick(e) {
      const fileInputEl = this.$refs.fileInput.$el;
      if (!fileInputEl.contains(e.target)) {
        this.$refs.fileInput.$refs.input.click();
      }
    },

    async onFileSelected(selectedFile) {
      if (!selectedFile) return;
      await this.parseFile(selectedFile);
    },

    async onDrop(e) {
      this.isDragging = false;
      const droppedFile = e.dataTransfer.files[0];
      if (!droppedFile) return;
      this.file = droppedFile;
      await this.parseFile(droppedFile);
    },

    async parseFile(rawFile) {
      this.parseError = null;
      this.workbook = null;
      this.sheetNames = [];
      this.selectedSheet = null;
      this.fileColumns = [];
      this.fileRows = [];
      this.mapping = buildEmptyMapping(this.requiredFields);

      if (rawFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        this.parseError = `El archivo supera el límite de ${MAX_FILE_SIZE_MB} MB. Divide el archivo en partes más pequeñas.`;
        return;
      }

      try {
        const arrayBuffer = await rawFile.arrayBuffer();
        // Frozen so Vue doesn't walk every cell of the workbook to make it reactive.
        this.workbook = Object.freeze(XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' }));
        this.sheetNames = this.workbook.SheetNames;
      } catch {
        this.parseError = 'No se pudo leer el archivo. Verifica que sea un .xlsx, .xls o .csv válido.';
        return;
      }

      const firstSheetWithData = this.sheetNames.find(name => this.readSheetRows(name).length >= 2);
      this.loadSheet(firstSheetWithData || this.sheetNames[0]);
    },

    readSheetRows(sheetName) {
      const sheet = this.workbook.Sheets[sheetName];
      return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) || [];
    },

    loadSheet(sheetName) {
      this.selectedSheet = sheetName;
      this.parseError = null;
      this.fileColumns = [];
      this.fileRows = [];
      this.mapping = buildEmptyMapping(this.requiredFields);

      const rows = this.readSheetRows(sheetName);
      if (rows.length < 2) {
        this.parseError = `La hoja "${sheetName}" no contiene datos. Se necesita al menos una fila de encabezado y una de datos.`;
        return;
      }

      this.fileColumns = rows[0].map(c => String(c).trim()).filter(Boolean);
      this.fileRows = rows.slice(1).filter(row => row.some(cell => cell !== ''));
      this.autoMap();
    },

    autoMap() {
      const lowerCols = this.fileColumns.map(c => c.toLowerCase());
      this.requiredFields.forEach(field => {
        const candidates = COLUMN_ALIASES[field.key] || [field.key];
        const idx = lowerCols.findIndex(c => candidates.includes(c));
        if (idx !== -1) {
          this.mapping[field.key] = field.multiple ? [this.fileColumns[idx]] : this.fileColumns[idx];
        }
      });
    },

    buildPreview() {
      this.preview = this.fileRows
        .map(row => {
          const entry = {};
          this.requiredFields.forEach(field => {
            const colName = this.mapping[field.key];
            let value;
            if (field.multiple) {
              value = (colName || [])
                .map(col => String(row[this.fileColumns.indexOf(col)] ?? '').trim())
                .filter(Boolean)
                .join(field.separator ?? ' ');
            } else {
              const colIdx = colName ? this.fileColumns.indexOf(colName) : -1;
              value = colIdx !== -1 ? String(row[colIdx] ?? '').trim() : '';
            }
            value = value.replace(/[,;]/g, '');
            const normalize = NORMALIZERS[field.key];
            entry[field.key] = normalize ? normalize(value) : value;
          });
          if (entry.nombreCompleto) {
            Object.assign(entry, splitFullName(entry.nombreCompleto));
          }
          return entry;
        })
        .filter(entry => Object.values(entry).some(v => v !== ''));
    },

    // planId de la fila según la relación de planes; null = acceso sin plan.
    rowPlanId(row) {
      const relation = this.planRelation[valueKey(row.plan)];
      return relation && relation !== ALL_PLANS ? relation : null;
    },

    planName(planId) {
      if (!planId) return 'Todos los planes';
      const plan = this.contratoPlanes.find(p => p.planId === planId);
      return plan ? plan.plan.name : planId;
    },

    // Fila de altas/nómina para el backend:
    // nombre,apellido,email,rut,extraInfo,planId,apellidoMaterno,fechaNacimiento,telefono,genero.
    // Los campos vacíos del final se omiten; el backend los toma como no informados.
    toEnrollLine(row) {
      const fields = [
        row.nombre, row.apellido, row.email, row.rut,
        row.extraInfo, this.withPlan ? this.rowPlanId(row) : '',
        row.apellidoMaterno, row.fechaNacimiento, row.telefono, this.withGender ? this.rowGender(row) : '',
      ].map(value => value || '');
      while (fields.length > 4 && !fields[fields.length - 1]) fields.pop();
      return fields.join(',');
    },

    previewCell(row, field) {
      if (field.key === 'plan' && this.withPlan) return this.planName(this.rowPlanId(row));
      if (field.key === 'genero' && this.withGender) {
        const option = GENDER_OPTIONS.find(o => o.value === this.rowGender(row));
        return option ? option.text : '';
      }
      return row[field.key];
    },

    fieldLabel(field) {
      if (field.optional) return `${field.label} (opcional)`;
      return field.multiple ? `${field.label} * (puedes elegir varias)` : `${field.label} *`;
    },

    buildAltasPayload() {
      const users = this.preview.flatMap(r => repeatByPets(this.toEnrollLine(r), r));
      return {
        contratoId: this.params.contratoId,
        users,
        ...(this.withPlan && { withPlan: true }),
      };
    },

    buildBajasPayload() {
      const rows = this.preview.filter(r => r.rut);
      return {
        contratoId: this.params.contratoId,
        ruts: rows.flatMap(r => repeatByPets(this.withPlan ? `${r.rut},${this.rowPlanId(r) || ''}` : r.rut, r)),
        ...(this.withPlan && { withPlan: true }),
      };
    },

    async onGoToPreview() {
      this.buildPreview();
      if (!this.withRelations) {
        await this.goToPreview();
        return;
      }
      this.step = RELATION_STEP;
      if (this.withGender) this.suggestGenderRelation();
      if (this.withPlan) await this.loadContratoPlanes();
    },

    // Conserva lo ya elegido y sugiere solo los valores inequívocos (ej. "Femenino").
    suggestGenderRelation() {
      const relation = {};
      this.genderValues.forEach(item => {
        relation[item.key] = this.genderRelation[item.key] || suggestGender(item.key);
      });
      this.genderRelation = relation;
    },

    // Género de la fila según la relación; '' = sin especificar.
    rowGender(row) {
      const gender = this.genderRelation[valueKey(row.genero)];
      return gender && gender !== NO_GENDER ? gender : '';
    },

    async goToPreview() {
      this.step = this.previewStep;
      if (this.category === 'nomina') {
        await this.fetchNominaDiff();
      }
    },

    // Planes visibles del contrato, y sugerencia del plan cuyo nombre coincide con el valor.
    async loadContratoPlanes() {
      this.loadingContratoPlanes = true;
      const res = await ContratoService.getById(this.params.contratoId);
      const planes = res.status === 'ok' && Array.isArray(res.data?.planes) ? res.data.planes : [];
      this.contratoPlanes = planes.filter(p => p.visible);
      const relation = {};
      this.planValues.forEach(item => {
        const match = this.contratoPlanes.find(p => valueKey(p.plan.name) === item.key);
        relation[item.key] = this.planRelation[item.key] || (match ? match.planId : undefined);
      });
      this.planRelation = relation;
      this.loadingContratoPlanes = false;
    },

    async fetchNominaDiff() {
      this.nominaDiffData = null;
      this.nominaDiffLoading = true;
      this.submitError = null;
      const users = this.preview.flatMap(r => repeatByPets(this.toEnrollLine(r), r));
      const res = await CargasMasivasService.nominaDiff({
        contratoId: this.params.contratoId,
        ruts: this.preview.map(r => r.rut),
        users,
        ...(this.withPlan && { withPlan: true }),
      });
      if (res.status === 'ok') {
        this.nominaDiffData = res.data;
      } else {
        this.submitError = 'No se pudo calcular la diferencia. Verifica el contrato e intenta de nuevo.';
      }
      this.nominaDiffLoading = false;
    },

    async submit() {
      this.submitting = true;
      this.submitError = null;
      this.progressCurrent = 0;
      this.progressTotal = 0;

      const onProgress = (event, offset = 0) => {
        this.progressCurrent = offset + event.processed;
        this.progressTotal = offset + event.total;
        this.$eventBus.$emit('bulk-progress', { current: this.progressCurrent, total: this.progressTotal });
      };

      if (this.category === 'altas') {
        this.$eventBus.$emit('bulk-start', { total: this.preview.length });
        const res = await CargasMasivasService.bulkAltas(this.buildAltasPayload(), {
          onProgress: e => onProgress(e),
        });
        if (res.status === 'ok') this.result = res.data;
        else this.submitError = 'Ocurrió un error al procesar la carga. Verifica los datos e intenta de nuevo.';

      } else if (this.category === 'bajas') {
        this.$eventBus.$emit('bulk-start', { total: this.preview.length });
        const res = await CargasMasivasService.bulkBajas(this.buildBajasPayload(), {
          onProgress: e => onProgress(e),
        });
        if (res.status === 'ok') this.result = res.data;
        else this.submitError = 'Ocurrió un error al procesar la carga. Verifica los datos e intenta de nuevo.';

      } else if (this.category === 'nomina') {
        // Altas antes que bajas: las altas restringen los accesos sin plan que las bajas descuentan.
        const base = { contratoId: this.params.contratoId, ...(this.withPlan && { withPlan: true }) };
        const toAddCount = (this.nominaDiffData.toAdd || []).length;
        const toRemoveCount = (this.nominaDiffData.toRemove || []).length;
        this.progressTotal = toAddCount + toRemoveCount;
        this.$eventBus.$emit('bulk-start', { total: this.progressTotal });

        const altasRes = toAddCount > 0
          ? await CargasMasivasService.bulkAltas({ ...base, users: this.nominaDiffData.toAdd }, {
              onProgress: e => onProgress(e, 0),
            })
          : { status: 'ok', data: { total: 0, created: 0, existing: 0, failed: 0, emailSent: 0, emailFailed: 0, failedEntries: [] } };

        const bajasRes = toRemoveCount > 0
          ? await CargasMasivasService.bulkBajas({ ...base, ruts: this.nominaDiffData.toRemove }, {
              onProgress: e => onProgress(e, toAddCount),
            })
          : { status: 'ok', data: { total: 0, fullyDeactivated: 0, decremented: 0, notFound: 0, entries: [] } };

        if (altasRes.status === 'ok' && bajasRes.status === 'ok') {
          this.result = { altas: altasRes.data, bajas: bajasRes.data };
        } else {
          this.submitError = 'Ocurrió un error al procesar la nómina. Verifica los datos e intenta de nuevo.';
        }
      }

      this.submitting = false;
      if (this.result) {
        const isError = this.failedCount > 0;
        const message = isError
          ? `${this.failedCount} registro(s) no pudieron procesarse.`
          : this.category === 'altas'
            ? `${this.result.created} usuario(s) cargado(s) correctamente.`
            : this.category === 'bajas'
              ? `${this.result.fullyDeactivated} usuario(s) dado(s) de baja correctamente.`
              : `Nómina aplicada correctamente.`;
        this.$eventBus.$emit('bulk-complete', { message, isError });
        if (isError) this.showErrorSnackbar(message);
        else this.showSuccessSnackbar(message);
      } else {
        this.$eventBus.$emit('bulk-complete', { message: 'Error al procesar la carga.', isError: true });
      }
    },

    openConfirm() {
      const contrato = this.selectedContrato?.label || 'contrato seleccionado';
      const archivo = this.file?.name || 'archivo';
      if (this.category === 'altas') {
        this.confirmMessage = `¿Estás seguro que deseas dar de alta ${this.preview.length} usuario(s) al contrato "${contrato}" usando el archivo "${archivo}"? Esta acción no se puede deshacer fácilmente.`;
      } else if (this.category === 'bajas') {
        this.confirmMessage = `¿Estás seguro que deseas dar de baja ${this.preview.length} usuario(s) del contrato "${contrato}" usando el archivo "${archivo}"? Esta acción desactivará sus membresías activas.`;
      } else {
        const toAdd = this.nominaDiffData?.summary?.toAddCount || 0;
        const toRemove = this.nominaDiffData?.summary?.toRemoveCount || 0;
        this.confirmMessage = `¿Estás seguro que deseas subir la nómina "${archivo}" al contrato "${contrato}"? Se darán de alta ${toAdd} y de baja ${toRemove} usuario(s).`;
      }
      this.confirmDialog = true;
    },

    onConfirmed() {
      this.confirmDialog = false;
      this.submit();
    },

    downloadFailedCSV() {
      const headers = ['nombre', 'apellido', 'email', 'rut', 'razón'];
      let rows = [];

      if (this.category === 'altas') {
        rows = (this.result.failedEntries || []).map(e => [e.name, e.surname, e.email, e.rut, e.reason]);
      } else if (this.category === 'bajas') {
        rows = (this.result.entries || []).filter(e => e.reason).map(e => ['', '', '', e.rut, e.reason]);
      } else if (this.category === 'nomina') {
        const altasFailed = (this.result.altas?.failedEntries || []).map(e => [e.name, e.surname, e.email, e.rut, e.reason]);
        const bajasFailed = (this.result.bajas?.entries || []).filter(e => e.reason).map(e => ['', '', '', e.rut, e.reason]);
        rows = [...altasFailed, ...bajasFailed];
      }

      const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fallidos_${this.category}_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    resetAll() {
      this.step = 1;
      this.category = null;
      this.params = buildEmptyParams();
      this.file = null;
      this.isDragging = false;
      this.parseError = null;
      this.workbook = null;
      this.sheetNames = [];
      this.selectedSheet = null;
      this.fileColumns = [];
      this.fileRows = [];
      this.mapping = {};
      this.preview = [];
      this.submitting = false;
      this.submitError = null;
      this.result = null;
      this.nominaDiffData = null;
      this.nominaDiffLoading = false;
      this.contratoPlanes = [];
      this.planRelation = {};
      this.genderRelation = {};
      this.confirmDialog = false;
      this.confirmMessage = '';
    },
  },
};
</script>

<style scoped>
.cargas-masivas {
  width: 100%;
}

/* ── Indicador de pasos ── */
.cargas-masivas__steps {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  gap: 4px;
}

.cargas-masivas__step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.cargas-masivas__step-item:not(:last-child)::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e0e0e0;
  margin: 0 8px;
}

.cargas-masivas__step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e0e0e0;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.cargas-masivas__step-item--active .cargas-masivas__step-circle {
  background: var(--v-primary-base);
  color: white;
}

.cargas-masivas__step-item--done .cargas-masivas__step-circle {
  background: #4caf50;
  color: white;
}

.cargas-masivas__step-label {
  font-size: 13px;
  color: #9e9e9e;
  white-space: nowrap;
}

.cargas-masivas__step-item--active .cargas-masivas__step-label {
  color: #1b1d21;
  font-weight: 600;
}

.cargas-masivas__step-item--done .cargas-masivas__step-label {
  color: #4caf50;
}

/* ── Cards de categoría ── */
.cargas-masivas__cat-card {
  cursor: pointer;
  transition: border-color 0.2s;
}

.cargas-masivas__cat-card--selected {
  border-color: var(--v-primary-base) !important;
  background-color: #e3f2fd;
}

/* ── Drop zone ── */
.cargas-masivas__drop-zone {
  border: 2px dashed #bdbdbd;
  border-radius: 8px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: border-color 0.2s, background-color 0.2s;
  cursor: pointer;
}

.cargas-masivas__drop-zone--active {
  border-color: var(--v-primary-base);
  background-color: #e3f2fd;
}

/* ── Labels ── */
.cargas-masivas__section-label {
  font-size: 14px;
  font-weight: 600;
  color: #1b1d21;
}
</style>
